// Calculs du cockpit : valeurs du mois, écarts au budget, atterrissage, alertes et benchmark.

import { entityByCockpit } from '../entities.js';
import type { CockpitDataset, CockpitEntity } from './types.js';

export const COCKPIT_RULES = {
  dsoTarget: 60,
  /** Écart de taux de REX déclenchant une alerte, en points. */
  rexGapPts: -2,
  /** DSO au-delà de l'objectif déclenchant une alerte, en jours. */
  dsoGapDays: 15,
  /** Retard des entrées de commandes sur le rythme attendu, en points. */
  ordersGapPts: -10,
  /** Seuils de statut d'atterrissage, en % d'écart projeté. */
  landingOk: -2,
  landingWatch: -8,
} as const;

export const MONTHS_SHORT = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];
export const MONTHS_FULL = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/** Dernier jour du mois (1 à 12) pour une année donnée. */
export const lastDay = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

export interface MonthValues {
  /** Index 0-based du mois retenu. */
  mi: number;
  month: number;
  ca: number;
  caBudget: number;
  marge: number;
  margeBudget: number;
  rex: number;
  rexBudget: number;
  margePct: number;
  tauxRex: number;
  tauxRexBudget: number;
}

/** Valeurs cumulées à la fin du mois demandé (borné aux mois disponibles). */
export function monthValues(d: CockpitEntity, month: number): MonthValues {
  const mi = Math.max(0, Math.min(month - 1, d.monthsAvail.length - 1));
  const at = (s: number[]) => s[mi] ?? 0;
  const ca = at(d.monthly.ca.actual);
  const caBudget = at(d.monthly.ca.budget);
  const marge = at(d.monthly.margeBrute.actual);
  const margeBudget = at(d.monthly.margeBrute.budget);
  const rex = at(d.monthly.rex.actual);
  const rexBudget = at(d.monthly.rex.budget);
  return {
    mi,
    month: d.monthsAvail[mi] ?? mi + 1,
    ca,
    caBudget,
    marge,
    margeBudget,
    rex,
    rexBudget,
    margePct: ca ? (marge / ca) * 100 : 0,
    tauxRex: ca ? (rex / ca) * 100 : 0,
    tauxRexBudget: caBudget ? (rexBudget / caBudget) * 100 : 0,
  };
}

export type Tone = 'good' | 'bad' | 'flat';

export interface Delta {
  value: number;
  /** Écart relatif en %, null si la référence est trop faible pour être parlante. */
  pct: number | null;
  tone: Tone;
}

/** Écart à une référence. `lowerIsBetter` pour un coût ou un délai. */
export function delta(
  actual: number,
  ref: number,
  opts: { lowerIsBetter?: boolean; epsilon?: number; minRef?: number } = {},
): Delta {
  const value = actual - ref;
  const eps = opts.epsilon ?? 0.005;
  const fav = opts.lowerIsBetter ? value < 0 : value > 0;
  const minRef = opts.minRef ?? 0.3;
  return {
    value,
    pct: Math.abs(ref) > minRef ? (value / Math.abs(ref)) * 100 : null,
    tone: Math.abs(value) < eps ? 'flat' : fav ? 'good' : 'bad',
  };
}

/** Part de l'année écoulée, en %, au rythme linéaire. */
export const pacing = (d: CockpitEntity): number => (d.monthsAvail.length / 12) * 100;

export type LandingStatus = 'ok' | 'watch' | 'off';

export const LANDING_LABELS: Record<LandingStatus, string> = {
  ok: 'En ligne',
  watch: 'À surveiller',
  off: 'Décroché',
};

export interface LandingRow {
  key: string;
  label: string;
  ytd: number;
  budget: number;
  /** Part du budget annuel déjà réalisée, en %. */
  pctOfBudget: number;
  /** Projection annualisée au rythme actuel. */
  projection: number;
  /** Écart projeté au budget, en %. */
  gapPct: number;
  status: LandingStatus;
  lowerIsBetter: boolean;
}

export function landingRow(
  key: string,
  label: string,
  ytd: number,
  budget: number,
  months: number,
  lowerIsBetter = false,
): LandingRow {
  const projection = months ? (ytd / months) * 12 : 0;
  const pctOfBudget = budget ? (ytd / budget) * 100 : 0;
  const gapPct = budget ? ((projection - budget) / Math.abs(budget)) * 100 : 0;
  const signed = lowerIsBetter ? -gapPct : gapPct;
  const status: LandingStatus =
    signed >= COCKPIT_RULES.landingOk ? 'ok' : signed >= COCKPIT_RULES.landingWatch ? 'watch' : 'off';
  return { key, label, ytd, budget, pctOfBudget, projection, gapPct, status, lowerIsBetter };
}

export function landing(d: CockpitEntity): LandingRow[] {
  const n = d.monthsAvail.length;
  const last = monthValues(d, n);
  const rows = [
    landingRow('caChantier', 'CA Chantier', d.chantier.ca, d.chantier.caBudget, n),
    landingRow('caSav', 'CA SAV', d.sav.ca, d.sav.caBudget, n),
    landingRow('ca', 'CA total', last.ca, d.atterrissage.ca, n),
    landingRow('marge', 'Marge brute', last.marge, d.atterrissage.margeBrute, n),
  ];
  if (d.ms) rows.push(landingRow('ms', 'Masse salariale', d.ms.ytd, d.ms.budgetAnnuel, n, true));
  rows.push(landingRow('rex', 'REX', last.rex, d.atterrissage.rex, n));
  return rows;
}

export type AlertSeverity = 'bad' | 'warn' | 'good';

export interface CockpitAlert {
  severity: AlertSeverity;
  text: string;
}

const fr1 = (v: number) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
const frM = (v: number) =>
  `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M€`;

/** Priorités du mois : au plus quatre signaux, les plus graves d'abord. */
export function computeAlerts(d: CockpitEntity, month: number): CockpitAlert[] {
  const v = monthValues(d, month);
  const alerts: CockpitAlert[] = [];
  const rexGap = v.tauxRex - v.tauxRexBudget;
  if (rexGap < COCKPIT_RULES.rexGapPts)
    alerts.push({
      severity: 'bad',
      text: `Taux de REX en retrait de ${fr1(Math.abs(rexGap))} pt sur le budget`,
    });
  const cash = d.cashBridge ? d.cashBridge.delta_cash_reel / 1e6 : 0;
  if (cash < 0)
    alerts.push({ severity: 'bad', text: `Trésorerie en baisse de ${frM(Math.abs(cash))} depuis décembre` });
  if (d.dso.jul != null) {
    const gap = d.dso.jul - COCKPIT_RULES.dsoTarget;
    if (gap > COCKPIT_RULES.dsoGapDays)
      alerts.push({
        severity: 'warn',
        text: `DSO à ${Math.round(d.dso.jul)} j, soit ${Math.round(gap)} j au-dessus de l'objectif de ${COCKPIT_RULES.dsoTarget} j`,
      });
  }
  if (d.commercial) {
    const done = (d.commercial.entrees_ytd / d.commercial.objectif_annuel) * 100;
    const gap = done - pacing(d);
    if (gap < COCKPIT_RULES.ordersGapPts)
      alerts.push({
        severity: 'warn',
        text: `Entrées de commandes à ${Math.round(done)} % de l'objectif annuel, ${Math.abs(Math.round(gap))} pt sous le rythme attendu`,
      });
  }
  const order: Record<AlertSeverity, number> = { bad: 0, warn: 1, good: 2 };
  alerts.sort((a, b) => order[a.severity] - order[b.severity]);
  if (!alerts.length)
    alerts.push({ severity: 'good', text: 'Aucun signal d’alerte ce mois-ci sur les indicateurs suivis' });
  return alerts.slice(0, 4);
}

export interface ConsolidatedRow {
  key: string;
  name: string;
  ca: number;
  /** CA cumulé en % du budget annuel. */
  caPct: number;
  margePct: number;
  margeBudgetPct: number;
  rex: number;
  /** REX budgété proratisé au nombre de mois écoulés. */
  rexBudget: number;
  tauxRex: number;
  tauxRexBudget: number;
  dso: number | null;
  ordersPct: number | null;
}

/** Benchmark entre entités suivies, trié par CA décroissant. */
export function consolidate(order: string[], data: Record<string, CockpitEntity>): ConsolidatedRow[] {
  return order
    .filter((k) => data[k])
    .map((k) => {
      const d = data[k] as CockpitEntity;
      const v = monthValues(d, d.monthsAvail.length);
      const at = d.atterrissage;
      return {
        key: k,
        name: d.name,
        ca: v.ca,
        caPct: at.ca ? (v.ca / at.ca) * 100 : 0,
        margePct: v.margePct,
        margeBudgetPct: at.ca ? (at.margeBrute / at.ca) * 100 : 0,
        rex: v.rex,
        rexBudget: at.rex * (d.monthsAvail.length / 12),
        tauxRex: v.tauxRex,
        tauxRexBudget: at.tauxRex,
        dso: d.dso.jul,
        ordersPct: d.commercial ? (d.commercial.entrees_ytd / d.commercial.objectif_annuel) * 100 : null,
      };
    })
    .sort((a, b) => b.ca - a.ca);
}

/** Série DSO triée par mois. */
export function dsoSeries(d: CockpitEntity): { month: number; dso: number }[] {
  return Object.entries(d.dso.series)
    .map(([m, v]) => ({ month: Number(m), dso: v }))
    .sort((a, b) => a.month - b.month);
}

/** CA mensuel net (factures moins avoirs, TotalHT) par établissement puis par année : 12 montants en euros. */
export interface SyncCa {
  monthly: Record<string, Record<string, number[]>>;
  /** Date de la dernière facture prise en compte (AAAA-MM-JJ). */
  asOf: string | null;
}

const r1 = (x: number) => Math.round(x * 10) / 10;
const r3 = (x: number) => Math.round(x * 1000) / 1000;

/**
 * Rattache à chaque entité (liée à un établissement SYNC) son CA mensuel par année, et remplace le CA cumulé
 * de l'année du reporting par celui des factures et avoirs clients, sur les mois clôturés en comptabilité.
 * Marge et REX restent comptables ; les taux cumulés sont recalculés sur le nouveau CA.
 * Les entités sans établissement sont inchangées.
 */
export function applySyncCa(d: CockpitDataset, ca: SyncCa): CockpitDataset {
  let used = false;
  const years = new Set<number>([d.year]);
  const data = { ...d.data };
  for (const [key, e] of Object.entries(d.data)) {
    const etab = entityByCockpit(key)?.caEtab;
    const byYear = etab ? ca.monthly[etab] : undefined;
    if (!byYear) continue;
    used = true;
    for (const y of Object.keys(byYear)) years.add(Number(y));
    const months = byYear[String(d.year)] ?? Array<number>(12).fill(0);
    const cum: number[] = [];
    let run = 0;
    for (let m = 1; m <= 12; m++) cum.push((run += months[m - 1] ?? 0));
    const actual = e.monthsAvail.map((m) => r3((cum[m - 1] ?? 0) / 1e6));
    const ytd = actual[actual.length - 1] ?? 0;
    data[key] = {
      ...e,
      caSource: 'sync',
      caSync: byYear,
      monthly: { ...e.monthly, ca: { ...e.monthly.ca, actual } },
      kpi: {
        ...e.kpi,
        ca: { ...e.kpi.ca, ytd },
        margeBrute: { ...e.kpi.margeBrute, pct: ytd ? r1((e.kpi.margeBrute.ytd / ytd) * 100) : 0 },
        tauxRex: { ...e.kpi.tauxRex, ytd: ytd ? r1((e.kpi.rex.ytd / ytd) * 100) : 0 },
      },
    };
  }
  if (!used) return d;
  return {
    ...d,
    data,
    freshness: { ...d.freshness, sync: ca.asOf },
    years: [...years].sort((a, b) => b - a),
  };
}
